import React, { useState } from "react";

import zoniqLogo from "../../logos/zoniq.png";
import type { PromoterDisplayMode } from "../../types/rsvp";
import { isOtherInternational, nationalDigitsIfIndian } from "../../utils/phone";
import {
  computeSelectionTotal,
  countActiveTiers,
  formatTicketInr,
  formatTicketSummaryLine,
  sumSelectionParty,
  summarizeSelectionsByTier,
  type TicketSelectionRow,
  type TierOption,
} from "./zoniqTicketUtils";

type PromoterProps = {
  includePromoter: boolean;
  display: PromoterDisplayMode;
  /** Mandatory only where the flow says so (Guest List) — independent of how
   * prominently the field is displayed, since RSVP's code stays optional even
   * when the template shows it bold. */
  required: boolean;
  promoters: { id: string; name: string }[];
  promoterId: string;
  promoterText: string;
  onPromoterIdChange: (id: string) => void;
  onPromoterTextChange: (text: string) => void;
  readOnly?: boolean;
  /** Show validation as if the field had been blurred — set after a blocked submit attempt. */
  forceTouched?: boolean;
};

/** True when `text` matches a known promoter's name exactly (case/whitespace-insensitive). */
export function matchPromoterByText(
  text: string,
  promoters: { id: string; name: string }[],
): { id: string; name: string } | null {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return null;
  return promoters.find((p) => p.name.trim().toLowerCase() === normalized) ?? null;
}

const ZoniqPromoterField: React.FC<PromoterProps> = ({
  includePromoter,
  display,
  required,
  promoters,
  promoterText,
  onPromoterIdChange,
  onPromoterTextChange,
  readOnly = false,
  forceTouched = false,
}) => {
  const [open, setOpen] = useState(display === "bold");
  const [touched, setTouched] = useState(false);
  const isTouched = touched || forceTouched;

  if (!includePromoter || display === "hidden") return null;

  const showField = display === "bold" || open;
  const hasPromoterRoster = promoters.length > 0;
  const matched = hasPromoterRoster ? matchPromoterByText(promoterText, promoters) : null;
  const blank = promoterText.trim().length === 0;
  const invalid = hasPromoterRoster && isTouched && !matched && (!blank || required);

  return (
    <>
      {display === "collapsible" ? (
        <button
          type="button"
          className="zticket-promo-toggle"
          onClick={() => setOpen((v) => !v)}
          tabIndex={readOnly ? -1 : undefined}
        >
          {open ? "Hide promoter" : "+ I have a promoter code"}
        </button>
      ) : null}
      {showField ? (
        <>
          <input
            className="zticket-input"
            placeholder="Promoter code"
            value={promoterText}
            onChange={(e) => {
              onPromoterTextChange(e.target.value);
              onPromoterIdChange(matchPromoterByText(e.target.value, promoters)?.id ?? "");
            }}
            onBlur={() => setTouched(true)}
            aria-invalid={invalid}
            required={!readOnly && required}
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
          />
          {invalid ? (
            <span className="zticket-error">
              {blank
                ? "Enter a valid promoter code to join this guest list."
                : "We couldn't find that promoter code — check the spelling."}
            </span>
          ) : null}
        </>
      ) : null}
    </>
  );
};

type SelectionBlockProps = {
  index: number;
  selection: TicketSelectionRow;
  tiers: TierOption[];
  canRemove: boolean;
  readOnly?: boolean;
  /** RSVP shows ticket prices; Guest List and Walk-in don't sell tickets, so they stay price-free. */
  showPricing?: boolean;
  onChange: (next: TicketSelectionRow) => void;
  onRemove: () => void;
};

const PARTY_CHIPS = [2, 4, 6, 10] as const;
const MAX_PARTY_SIZE = 99;

const SelectionBlock: React.FC<SelectionBlockProps> = ({
  index,
  selection,
  tiers,
  canRemove,
  readOnly = false,
  showPricing = false,
  onChange,
  onRemove,
}) => (
  <div className="zticket-selection">
    {/* Without a tier picker (Guest List) there is nothing to number. */}
    {tiers.length > 0 ? (
      <div className="zticket-selection__head">
        <span className="zticket-selection__title">SELECTION {index + 1}</span>
        {canRemove && !readOnly ? (
          <button type="button" className="zticket-selection__remove" onClick={onRemove}>
            Remove
          </button>
        ) : null}
      </div>
    ) : null}

    <div className="zticket-field zticket-field--party">
      <span className="zticket-field__label zticket-field__label--dim">HOW MANY OF YOU?</span>
      <div className="zticket-stepper">
        <button
          type="button"
          className="zticket-stepper__btn"
          onClick={() => onChange({ ...selection, partySize: Math.max(1, selection.partySize - 1) })}
          disabled={readOnly || selection.partySize <= 1}
          tabIndex={readOnly ? -1 : undefined}
        >
          –
        </button>
        <span className="zticket-stepper__value">{selection.partySize}</span>
        <button
          type="button"
          className="zticket-stepper__btn"
          onClick={() => onChange({ ...selection, partySize: Math.min(MAX_PARTY_SIZE, selection.partySize + 1) })}
          disabled={readOnly || selection.partySize >= MAX_PARTY_SIZE}
          tabIndex={readOnly ? -1 : undefined}
        >
          +
        </button>
      </div>
      <div className="zticket-chips">
        {PARTY_CHIPS.map((n) => (
          <button
            key={n}
            type="button"
            className={`zticket-chip${selection.partySize === n ? " is-active" : ""}`}
            onClick={() => onChange({ ...selection, partySize: n })}
            disabled={readOnly}
            tabIndex={readOnly ? -1 : undefined}
          >
            {n}
          </button>
        ))}
      </div>
    </div>

    {tiers.length > 0 ? (
      <div className="zticket-field">
        <span className="zticket-field__label zticket-field__label--dim">ENTRY TYPE</span>
        <div className="zticket-tier-grid">
          {tiers.map((tier) => (
            <button
              key={tier.id}
              type="button"
              className={`zticket-tier-btn${selection.tierId === tier.id ? " is-active" : ""}`}
              onClick={() => onChange({ ...selection, tierId: tier.id })}
              disabled={readOnly}
              tabIndex={readOnly ? -1 : undefined}
            >
              <span className="zticket-tier-btn__name">{tier.name}</span>
              {tier.description ? <span className="zticket-tier-btn__desc">{tier.description}</span> : null}
              {showPricing ? <span className="zticket-tier-btn__price">{formatTicketInr(tier.price)}</span> : null}
            </button>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

export type ZoniqTicketFormBodyProps = {
  tiers: TierOption[];
  selections: TicketSelectionRow[];
  onSelectionsChange?: (next: TicketSelectionRow[]) => void;
  name: string;
  phone: string;
  email?: string;
  onNameChange?: (v: string) => void;
  onPhoneChange?: (v: string) => void;
  onEmailChange?: (v: string) => void;
  /** Paying online needs an email for the receipt and pass, so the label stops saying optional. */
  emailRequired?: boolean;
  /** Guest List mode: ask party size only — no ticket tier picker or cost summary. */
  partySizeOnly?: boolean;
  includePromoter?: boolean;
  promoterDisplay?: PromoterDisplayMode;
  /** Guest List requires a promoter code; RSVP's stays optional. */
  promoterRequired?: boolean;
  promoters?: { id: string; name: string }[];
  promoterId?: string;
  promoterText?: string;
  onPromoterIdChange?: (id: string) => void;
  onPromoterTextChange?: (text: string) => void;
  /** Show promoter validation as if blurred — set after a blocked submit attempt. */
  promoterForceTouched?: boolean;
  /** RSVP shows ticket prices; Guest List and Walk-in don't sell tickets, so they stay price-free. */
  showPricing?: boolean;
  submitLabel: string;
  pending?: boolean;
  readOnly?: boolean;
  showFooter?: boolean;
  /** Inline error shown under the mobile number. */
  phoneError?: string | null;
  /** Rendered between the fields and the submit button (e.g. a consent checkbox). */
  extraFields?: React.ReactNode;
};

export const ZoniqTicketFormBody: React.FC<ZoniqTicketFormBodyProps> = ({
  tiers,
  selections,
  onSelectionsChange,
  name,
  phone,
  email = "",
  onNameChange,
  onPhoneChange,
  onEmailChange,
  emailRequired = false,
  partySizeOnly = false,
  includePromoter = false,
  promoterDisplay = "collapsible",
  promoterRequired = false,
  promoters = [],
  promoterId = "",
  promoterText = "",
  onPromoterIdChange,
  onPromoterTextChange,
  promoterForceTouched = false,
  showPricing = false,
  submitLabel,
  pending = false,
  readOnly = false,
  showFooter = true,
  phoneError,
  extraFields,
}) => {
  const partySize = sumSelectionParty(selections);
  const tierCount = countActiveTiers(selections);
  const total = computeSelectionTotal(selections, tiers);
  const tierBreakdown = summarizeSelectionsByTier(selections, tiers);
  // Guest List asks only "how many of you" — no tier picker, no cost summary.
  const showTierUi = tiers.length > 0 && !partySizeOnly;
  const showPartySize = showTierUi || partySizeOnly;
  const canAddSelection = showTierUi && !readOnly;

  const updateSelection = (id: string, next: TicketSelectionRow) => {
    onSelectionsChange?.(selections.map((s) => (s.id === id ? next : s)));
  };

  const removeSelection = (id: string) => {
    if (selections.length <= 1) return;
    onSelectionsChange?.(selections.filter((s) => s.id !== id));
  };

  const addSelection = () => {
    const firstTier = tiers[0]?.id ?? null;
    onSelectionsChange?.([
      ...selections,
      { id: `sel-${Date.now()}`, partySize: 1, tierId: firstTier },
    ]);
  };

  const invalidSubmit = partySizeOnly
    ? partySize < 1
    : tiers.length > 0 && (partySize < 1 || tierCount < 1);

  return (
    <div className={`zticket-form${readOnly ? " zticket-form--preview" : ""}`}>
      <div className="zticket-field">
        <label className="zticket-field__label" htmlFor={readOnly ? undefined : "zticket-name"}>
          YOUR NAME
        </label>
        <input
          id="zticket-name"
          className="zticket-input"
          value={name}
          onChange={(e) => onNameChange?.(e.target.value)}
          required={!readOnly}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
        />
      </div>

      <div className="zticket-field">
        <label className="zticket-field__label" htmlFor={readOnly ? undefined : "zticket-phone"}>
          MOBILE NUMBER
        </label>
        <div className="zticket-phone">
          {isOtherInternational(phone) ? null : <span className="zticket-phone__prefix">+91</span>}
          <input
            id="zticket-phone"
            className="zticket-input"
            inputMode="tel"
            autoComplete="tel"
            placeholder={isOtherInternational(phone) ? "+14155552671" : "98765 43210"}
            value={phone}
            onChange={(e) => {
              const next = e.target.value;
              onPhoneChange?.(nationalDigitsIfIndian(next) ?? next);
            }}
            required={!readOnly}
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
            aria-invalid={Boolean(phoneError)}
            aria-describedby={phoneError ? "zticket-phone-error" : undefined}
          />
        </div>
        {phoneError ? (
          <span id="zticket-phone-error" className="zticket-error" role="alert">
            {phoneError}
          </span>
        ) : (
          <p className="zticket-hint">India default is +91. You can also enter another country code, like +1…</p>
        )}
      </div>

      <div className="zticket-field">
        <label className="zticket-field__label" htmlFor={readOnly ? undefined : "zticket-email"}>
          {emailRequired ? "EMAIL" : "EMAIL (OPTIONAL)"}
        </label>
        <input
          id="zticket-email"
          className="zticket-input"
          type="email"
          value={email}
          onChange={(e) => onEmailChange?.(e.target.value)}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
        />
      </div>

      {showPartySize ? (
        <div className="zticket-selections">
          {selections.map((sel, index) => (
            <SelectionBlock
              key={sel.id}
              index={index}
              selection={sel}
              tiers={showTierUi ? tiers : []}
              canRemove={showTierUi && selections.length > 1}
              readOnly={readOnly}
              showPricing={showPricing}
              onChange={(next) => updateSelection(sel.id, next)}
              onRemove={() => removeSelection(sel.id)}
            />
          ))}
          {canAddSelection ? (
            <button type="button" className="zticket-add-selection" onClick={addSelection}>
              + Add selection
            </button>
          ) : null}
        </div>
      ) : (
        <p className="zticket-hint">No ticket tiers configured yet — party size defaults to 1.</p>
      )}

      {showTierUi ? (
        <div className="zticket-summary">
          <div className="zticket-summary__lines">
            {tierBreakdown.length === 0 ? (
              <span className="zticket-summary__line">{formatTicketSummaryLine(partySize, tierCount)}</span>
            ) : (
              tierBreakdown.map((row) => (
                <span key={row.tierId} className="zticket-summary__line">
                  {row.guests === 1 ? "1 guest" : `${row.guests} guests`} · {row.name}
                  {showPricing && tierBreakdown.length > 1 ? ` · ${formatTicketInr(row.amount)}` : ""}
                </span>
              ))
            )}
          </div>
          {showPricing ? <span className="zticket-summary__total">{formatTicketInr(total)}</span> : null}
        </div>
      ) : null}

      <ZoniqPromoterField
        includePromoter={includePromoter}
        display={promoterDisplay}
        required={promoterRequired}
        promoters={promoters}
        promoterId={promoterId}
        promoterText={promoterText}
        onPromoterIdChange={onPromoterIdChange ?? (() => undefined)}
        onPromoterTextChange={onPromoterTextChange ?? (() => undefined)}
        readOnly={readOnly}
        forceTouched={promoterForceTouched}
      />

      {extraFields}

      <button
        type={readOnly ? "button" : "submit"}
        className="zticket-submit"
        disabled={readOnly ? false : pending || invalidSubmit}
        tabIndex={readOnly ? -1 : undefined}
      >
        {pending ? "Submitting…" : submitLabel}
      </button>

      {showFooter ? (
        <footer className="zticket-footer">
          <p className="zticket-footer__note">Pay at the door · free cancellation until 6 PM</p>
          <div className="zticket-footer__badge">
            <span className="zticket-footer__badge-label">Powered by</span>
            <img className="zticket-footer__badge-logo" src={zoniqLogo} alt="Zoniq" />
          </div>
        </footer>
      ) : null}
    </div>
  );
};
